// API client for the MinerVibe backend (RAG Docente API — FastAPI).
//
// Base URL resolution:
// - En producción usamos el proxy de nginx (`/api`) para evitar problemas de CORS.
// - En desarrollo usamos el valor inyectado en build time por Vite (`__API_BASE_URL__`),
//   configurable mediante la variable de entorno `API_BASE_URL` (default: backend de traefik;
//   usa `API_BASE_URL=http://localhost:8000` para un backend local).
//
// Casi todos los endpoints cuelgan del prefijo `/v1` (excepto materiales y health).

import { getSubjectCodeFromName } from '../config/subjects';

const isProduction =
  window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1';

// `__API_BASE_URL__` es un define de Vite (token literal sustituido en build).
const DEV_API_BASE_URL =
  typeof __API_BASE_URL__ !== 'undefined' && __API_BASE_URL__
    ? __API_BASE_URL__
    : 'http://ragua-backend-stti7x-f5a111-62-84-178-121.traefik.me';

const API_BASE_URL = isProduction ? '/api' : DEV_API_BASE_URL;

/* ─── Error handling ─────────────────────────────────────────────────────── */

/**
 * Error de la API. El backend devuelve `{ detail: string, extra?: object }`
 * con códigos HTTP 4xx/5xx (en los 422 de validación, `detail` es un array).
 */
export class ApiError extends Error {
  status: number;
  detail: string;
  extra?: Record<string, unknown>;

  constructor(status: number, detail: string, extra?: Record<string, unknown>) {
    super(detail);
    this.name = 'ApiError';
    this.status = status;
    this.detail = detail;
    this.extra = extra;
  }
}

/* ─── Common types ───────────────────────────────────────────────────────── */

export interface Citation {
  source: string;
  page: number | null;
  section: string | null;
  chunk_id: string;
  quote: string;
  relevance: number;
}

export type ResponseMode = 'adaptive' | 'concise' | 'deep';
export type ReasoningQuality = 'high' | 'medium' | 'low';

/* ─── Chat ───────────────────────────────────────────────────────────────── */

export interface ChatRequestV1 {
  subject: string;
  message: string;
  thread_id?: string | null;
  user_id?: string | null;
  response_mode?: ResponseMode;
}

export interface ChatResponseV1 {
  answer: string;
  citations: Citation[];
  thread_id: string;
  query_id: string | null;
  follow_up_questions: string[];
  confidence: number;
  reasoning_quality: ReasoningQuality;
}

// Alias de compatibilidad para código existente.
export type ChatResponse = ChatResponseV1;
export type ChatRequest = ChatRequestV1;

/* ─── Feedback ───────────────────────────────────────────────────────────── */

// El backend solo acepta feedback sobre respuestas del chat.
export type FeedbackTargetType = 'chat';
export type FeedbackRating = 'up' | 'down';

export interface FeedbackRequest {
  target_type?: FeedbackTargetType; // default "chat"
  target_id: string;
  rating: FeedbackRating;
  reason?: string | null;
  subject?: string | null;
}

export interface FeedbackResponse {
  status: string;
  persisted: boolean;
}

/* ─── Materials ──────────────────────────────────────────────────────────── */

export interface Material {
  name: string;
  path: string;
  url: string;
  subject: string;
  type: string;
}

export interface MaterialsResponse {
  total: number;
  materials: Material[];
}

/* ─── Core request helper ────────────────────────────────────────────────── */

/**
 * Realiza una petición JSON contra la API y devuelve el cuerpo tipado.
 * Centraliza el manejo de errores `{ detail, extra? }` y la validación de
 * `content-type` para todos los endpoints.
 */
async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const url = `${API_BASE_URL}${path}`;

  const response = await fetch(url, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...(init?.headers ?? {}),
    },
  });

  if (!response.ok) {
    const raw = await response.text();
    let detail = raw || `HTTP ${response.status}`;
    let extra: Record<string, unknown> | undefined;
    try {
      const parsed = JSON.parse(raw) as { detail?: unknown; extra?: Record<string, unknown> };
      if (typeof parsed.detail === 'string' && parsed.detail.trim()) {
        detail = parsed.detail;
      } else if (Array.isArray(parsed.detail)) {
        // Errores de validación de FastAPI (422): [{ loc, msg, type, ... }]
        const messages = parsed.detail
          .map((item) => {
            const { loc, msg } = (item ?? {}) as { loc?: unknown[]; msg?: unknown };
            if (typeof msg !== 'string') return null;
            const field = Array.isArray(loc) ? loc.filter((p) => p !== 'body').join('.') : '';
            return field ? `${field}: ${msg}` : msg;
          })
          .filter(Boolean);
        if (messages.length > 0) {
          detail = messages.join('; ');
        }
      }
      if (parsed.extra && typeof parsed.extra === 'object') {
        extra = parsed.extra;
      }
    } catch {
      // El cuerpo no era JSON; mantenemos el texto original.
    }
    console.error('[API] Error response:', response.status, detail);
    throw new ApiError(response.status, detail, extra);
  }

  const contentType = response.headers.get('content-type');
  if (!contentType || !contentType.includes('application/json')) {
    const text = await response.text();
    console.error('[API] Expected JSON but got:', contentType, text.substring(0, 200));
    throw new ApiError(
      response.status,
      `Respuesta inesperada del servidor (content-type: ${contentType ?? 'desconocido'}).`
    );
  }

  return (await response.json()) as T;
}

/* ─── Chat ───────────────────────────────────────────────────────────────── */

/**
 * Envía un mensaje de chat. Convierte el nombre de la asignatura a su código
 * de API antes de enviar y devuelve la respuesta con `thread_id` y `query_id`.
 */
export async function sendChatMessage(
  query: string,
  subject: string,
  chatId?: string | null,
  responseMode: ResponseMode = 'adaptive',
  userId?: string | null
): Promise<ChatResponseV1> {
  const subjectCode = getSubjectCodeFromName(subject);
  const threadIdValue = chatId && chatId.trim() !== '' ? chatId : null;

  const body: ChatRequestV1 = {
    subject: subjectCode,
    message: query,
    thread_id: threadIdValue,
    user_id: userId ?? null,
    response_mode: responseMode,
  };

  return request<ChatResponseV1>('/v1/chat', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/* ─── Feedback ───────────────────────────────────────────────────────────── */

/**
 * Envía feedback (👍/👎) sobre una respuesta del chat (`target_id` = `query_id`).
 */
export async function sendFeedback(feedback: FeedbackRequest): Promise<FeedbackResponse> {
  const body: FeedbackRequest = {
    ...feedback,
    subject: feedback.subject ? getSubjectCodeFromName(feedback.subject) : feedback.subject ?? null,
  };

  return request<FeedbackResponse>('/v1/feedback', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/* ─── Materials ──────────────────────────────────────────────────────────── */

/**
 * Lista los materiales (documentos fuente) de una asignatura.
 * Endpoint sin prefijo `/v1`: `GET /materials?subject=CODE`.
 */
export async function fetchMaterials(subjectName: string): Promise<Material[]> {
  const subjectCode = getSubjectCodeFromName(subjectName);
  const data = await request<MaterialsResponse>(
    `/materials?subject=${encodeURIComponent(subjectCode)}`,
    { method: 'GET' }
  );
  return Array.isArray(data.materials) ? data.materials : [];
}

function safeDecodeURIComponent(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

/**
 * Construye la URL completa de un recurso de la API. El backend devuelve las
 * rutas de materiales sin codificar (`/materials/view/ALGOR/Tema 1 - Introducción.pdf`),
 * así que codificamos cada segmento del path (decodificando antes para no
 * codificar dos veces si ya venía codificado).
 */
function resolveApiUrl(url: string): string {
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }

  const encodedPath = url
    .replace(/\\/g, '/')
    .split('/')
    .map((segment) => encodeURIComponent(safeDecodeURIComponent(segment)))
    .join('/');

  if (encodedPath.startsWith('/api/')) {
    // Si la URL ya empieza con /api/, no duplicar el prefijo
    return encodedPath;
  }
  if (encodedPath.startsWith('/')) {
    return `${API_BASE_URL}${encodedPath}`;
  }
  return `${API_BASE_URL}/${encodedPath}`;
}

/**
 * Extrae el nombre de fichero de `Content-Disposition`. FastAPI usa
 * `filename*=utf-8''<percent-encoded>` cuando el nombre tiene espacios o
 * caracteres no ASCII, y `filename="<nombre>"` en el resto de casos.
 */
function parseContentDispositionFileName(header: string | null): string | null {
  if (!header) return null;

  const extended = header.match(/filename\*\s*=\s*([^;]+)/i);
  if (extended) {
    const value = extended[1].trim().replace(/^"(.*)"$/, '$1');
    const encoded = value.replace(/^[^']*'[^']*'/, '');
    const decoded = safeDecodeURIComponent(encoded);
    if (decoded) return decoded;
  }

  const plain = header.match(/filename\s*=\s*("([^"]*)"|[^;]+)/i);
  if (plain) {
    const value = (plain[2] ?? plain[1]).trim();
    if (value) return value;
  }

  return null;
}

/**
 * Download a file from the API.
 * This function properly handles binary content by using fetch and blob,
 * ensuring that the file is downloaded correctly instead of receiving HTML.
 */
export async function downloadFile(url: string, fileName?: string): Promise<void> {
  try {
    const fullUrl = resolveApiUrl(url);

    console.log('[API] Downloading file from:', fullUrl);

    const response = await fetch(fullUrl, {
      method: 'GET',
      headers: {
        Accept: '*/*', // Aceptar cualquier tipo de contenido
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('[API] Error downloading file:', response.status, errorText);
      throw new Error(
        `HTTP error when downloading file: ${response.status} - ${errorText.substring(0, 100)}`
      );
    }

    // Obtener el tipo de contenido de la respuesta
    const contentType = response.headers.get('content-type') || '';

    // Verificar si la respuesta es HTML (lo cual indicaría un error o redirección)
    if (contentType.includes('text/html')) {
      const htmlContent = await response.text();
      console.error('[API] Received HTML instead of file:', htmlContent.substring(0, 500));
      throw new Error(
        'El servidor devolvió HTML en lugar del archivo. Puede ser un error o una redirección.'
      );
    }

    // Obtener el blob de la respuesta usando clone() para poder leerlo múltiples veces si es necesario
    const responseClone = response.clone();
    const blob = await response.blob();

    // Verificación adicional: si el blob es pequeño, verificar si contiene HTML
    if (blob.size < 50000) {
      // Verificar solo para archivos pequeños (< 50KB)
      try {
        const text = await responseClone.text();
        const trimmedText = text.trim();
        // Verificar si el contenido parece HTML
        if (
          trimmedText.startsWith('<!DOCTYPE') ||
          trimmedText.startsWith('<html') ||
          trimmedText.startsWith('<!') ||
          (trimmedText.startsWith('<') && trimmedText.includes('</html>'))
        ) {
          console.error('[API] Received HTML content in blob:', trimmedText.substring(0, 500));
          throw new Error(
            'El servidor devolvió HTML en lugar del archivo. Puede ser un error o una redirección.'
          );
        }
      } catch (checkError) {
        // Si hay un error al leer el texto, continuar con el blob original
        if (checkError instanceof Error && checkError.message.includes('HTML')) {
          throw checkError;
        }
      }
    }

    // Nombre del archivo: el proporcionado (nombre exacto del material), luego
    // el header Content-Disposition y, como último recurso, la URL.
    let finalFileName =
      fileName || parseContentDispositionFileName(response.headers.get('content-disposition'));

    if (!finalFileName) {
      const lastSegment = url.split('?')[0].split(/[\\/]/).pop() || '';
      finalFileName = safeDecodeURIComponent(lastSegment) || 'download';
    }

    // Crear un enlace temporal y descargar el archivo
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = finalFileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(blobUrl);

    console.log('[API] File downloaded successfully:', finalFileName);
  } catch (error) {
    console.error('Error downloading file:', error);
    throw error;
  }
}
